import { Component, output, input, signal, inject, effect, untracked, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule, ClrLoadingState } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { Subject, firstValueFrom } from 'rxjs';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { VerwaltungsView, WartungsItem, WartungsMaske, WartungsMaskeItem } from '../wartung.types';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { type CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { ConfirmService } from 'src/app/services/confirm.service';
import { differenceBy } from 'src/app/misc/functions';

type UpdateStatus = 'new' | 'change' | 'copy';

@Component({
  selector: 'app-wartungsmasken',
  templateUrl: './wartungsmasken.component.html',
  styleUrls: ['./wartungsmasken.component.scss'],
  imports: [
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    AutofocusDirective,
    MrTranslatePipe,
    TablePrettyPrintPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WartungsMaskenComponent {
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly store = inject(BasicStoreService);
  private readonly api = inject(WartungApiService);
  private readonly confirmService = inject(ConfirmService);

  readonly view = output<VerwaltungsView>();
  readonly refresh = input<Subject<void>>(null);
  readonly otypid = input.required<number>();

  //#region masken
  protected maskenListe = signal<WartungsMaske[]>(null);
  protected maskenAddStatus = signal<UpdateStatus>(null);
  protected maskenNameNeu = signal<string>(null);
  protected isOpenModalMaskenName = signal(false);

  protected readonly selectedMaske = signal<number>(null);
  //#endregion
  //#region arten
  protected readonly allArten = signal<WartungsItem[]>(null);
  protected readonly notUsedArten = signal<WartungsItem[]>(null);
  protected readonly selectedLeft = signal<WartungsItem[]>([]);
  protected readonly moveRightState = signal<ClrLoadingState>(null);
  //#region rightTable
  protected readonly usedArten = signal<WartungsMaskeItem[]>(null);
  protected readonly selectedRight = signal<WartungsMaskeItem[]>([]);
  protected readonly moveLeftState = signal<ClrLoadingState>(null);

  protected readonly moveUpDownState = signal<ClrLoadingState>(null);
  protected readonly enableMove = signal({ up: false, down: false });
  private readonly countToMove = signal(0);
  //#endregion
  //#endregion
  protected readonly viewValid = signal(true);

  constructor() {
    effect(() => {
      if (this.selectedRight()) untracked(() => this.checkEnableUpDown());
    });
    effect(() => {
      untracked(() => this.initStates());
      if (this.selectedMaske()) untracked(() => this.loadMaskeItems());
    });
    effect(() => {
      if (this.otypid())
        untracked(async () => {
          this.allArten.set(null);
          this.usedArten.set(null);
          this.notUsedArten.set(null);
          this.maskenListe.set(null);
          this.selectedMaske.set(null);
          const arten = await firstValueFrom(this.api.getWartungsItem<WartungsItem>('art', this.otypid()));
          this.allArten.set(arten.rows);
          this.viewValid.set(!!this.allArten().length);
          if (this.viewValid()) this.loadMaskenList();
        });
    });
  }
  //#region load()
  private async loadMaskenList() {
    const masken = await firstValueFrom(this.api.getWartungsItem<WartungsMaske>('masken-names', this.otypid()));
    this.maskenListe.set(masken.rows);
    this.selectedMaske.set(this.maskenListe()[0]?.ID);
  }

  private async loadMaskeItems() {
    const arten = await firstValueFrom(this.api.getWartungsMaskeList(this.selectedMaske()));
    this.usedArten.set(arten);
    this.extractUsed();
  }
  //#endregion
  //#region Maske-Items()
  protected async moveToRight() {
    if (!this.selectedLeft().length) return;
    this.moveRightState.set(ClrLoadingState.LOADING);

    let maxSort = Math.max(...this.usedArten().map(art => art.rSort), 1_000_000);
    const arten = this.selectedLeft().map<WartungsMaskeItem>(art => ({
      ID: art.ID,
      Bezeichnung: art.Bezeichnung,
      Typ: art.Typ,
      rSort: ++maxSort,
    }));

    const { success, error, IDs } = await firstValueFrom(
      this.api.setWartungsMaskeList(this.selectedMaske(), this.otypid(), { arten })
    );

    if (success) {
      this.usedArten.update(used => {
        arten.forEach((art, i) => {
          used.push({ rID: IDs[i], ...art });
        });
        return [...used];
      });
      this.extractUsed();
      this.moveRightState.set(ClrLoadingState.SUCCESS);
    } else {
      this.moveRightState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async moveToLeft() {
    if (!this.selectedRight().length) return;
    this.moveLeftState.set(ClrLoadingState.LOADING);
    try {
      const result = await Promise.all(
        this.selectedRight().map(art => firstValueFrom(this.api.deleteWartungsItem('masken-items', art.rID)))
      );

      this.loadMaskeItems();
      if (result.every(({ success }) => success)) {
        this.moveLeftState.set(ClrLoadingState.SUCCESS);
      } else
        throw new Error(
          this.mrTranslate.transform('Nicht alle Einträge konnten gelöscht werden!') +
            this.mrTranslate.transform('Bitte versuchen Sie nochmal')
        );
    } catch (er) {
      this.moveLeftState.set(ClrLoadingState.DEFAULT);
      this.toastr.error((er as Error).message, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected moveUpDown(direction: 'up' | 'down') {
    this.checkEnableUpDown();
    if (!this.enableMove()[direction]) return;

    const selected = this.selectedRight()[0];
    this.moveOrder(selected, direction == 'up' ? -1 : 1);
  }

  protected moveDrop(event: CdkDragDrop<WartungsMaskeItem[]>) {
    if (event.previousContainer != event.container) return;

    const changeCount = event.currentIndex - event.previousIndex;
    if (changeCount == 0) return;

    for (let i = 0; i < Math.abs(changeCount); i++) {
      this.moveOrder(event.item.data, changeCount < 0 ? -1 : 1);
    }
  }

  private moveOrder(selected: WartungsMaskeItem, changeCount: -1 | 1) {
    const selectedIndex = this.usedArten().indexOf(selected);
    const currentCount = this.countToMove();

    this.usedArten.update(arten => {
      const swapWith = arten.at(selectedIndex + changeCount);
      const tmpSort = swapWith.rSort;
      swapWith.rSort = selected.rSort;
      selected.rSort = tmpSort;

      arten.sort((a, b) => a.rSort - b.rSort);
      return [...arten];
    });
    this.countToMove.set(currentCount + changeCount);

    if (currentCount == 0)
      setTimeout(async () => {
        const aktiveCount = this.countToMove();
        if (aktiveCount == 0) return;

        this.moveUpDownState.set(ClrLoadingState.LOADING);
        const arten =
          aktiveCount > 0 ?
            this.usedArten().slice(selectedIndex, selectedIndex + aktiveCount + 1)
          : this.usedArten().slice(selectedIndex + aktiveCount, selectedIndex + 1);

        const { success, error } = await firstValueFrom(
          this.api.setWartungsMaskeList(this.selectedMaske(), this.otypid(), { arten, edit: true })
        );

        if (success) {
          this.moveUpDownState.set(ClrLoadingState.SUCCESS);
        } else {
          this.moveUpDownState.set(ClrLoadingState.DEFAULT);
          this.loadMaskeItems();
          this.toastr.error(
            error,
            this.mrTranslate.transform('Etwas ist schief gelaufen') +
              '!  ' +
              this.mrTranslate.transform('Bitte versuchen Sie nochmal')
          );
        }
        this.countToMove.set(0);
      }, 1000);
  }
  //#endregion
  //#region Masken-Names()
  protected addMaske(mode: UpdateStatus) {
    this.maskenAddStatus.set(mode);
    this.isOpenModalMaskenName.set(true);
    this.maskenNameNeu.set(mode == 'new' ? null : this.maskenListe()?.find(m => m.ID == this.selectedMaske())?.Name);
  }

  protected async saveMaske() {
    const sendObj = {
      ID: this.maskenAddStatus() == 'change' ? this.selectedMaske() : null,
      Bezeichnung: this.maskenNameNeu(),
    };

    const { success, error, ...neuRow } = await firstValueFrom(
      this.api.setWartungsItem('masken-names', this.otypid(), sendObj)
    );

    if (success) {
      const { success, error } =
        this.maskenAddStatus() == 'copy' ?
          await firstValueFrom(this.api.setWartungsMaskeList(neuRow.ID, this.otypid(), { arten: this.usedArten() }))
        : { success: true };

      if (success) {
        this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
        if (this.maskenAddStatus() == 'change') {
          this.refresh()?.next();
          this.store.deleteComponentStoreAsync('wartung');
          this.maskenListe.update(liste => {
            const maske = liste.find(m => m.ID == this.selectedMaske());
            maske.Name = this.maskenNameNeu();
            return [...liste];
          });
        } else {
          this.maskenListe.update(
            liste => (liste.push({ Name: this.maskenNameNeu(), OTYPID: this.otypid(), ...neuRow }), [...liste])
          );
          this.selectedMaske.set(neuRow.ID);
        }
        this.isOpenModalMaskenName.set(false);
      } else {
        this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
      }
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteMaske() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;

    const result = await Promise.all([
      firstValueFrom(this.api.deleteWartungsItem('masken-names', this.selectedMaske())),
      firstValueFrom(this.api.deleteWartungsMaske(this.selectedMaske())),
    ]);

    if (result.every(({ success }) => success)) {
      this.refresh()?.next();
      this.store.deleteComponentStoreAsync('wartung');
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.maskenListe.update(liste => liste.filter(maske => maske.ID != this.selectedMaske()));
      this.selectedMaske.set(null);
    } else this.toastr.error(this.mrTranslate.transform('Fehler beim Löschen'));
  }
  //#endregion
  //#region helpers()
  private initStates() {
    this.selectedRight.set([]);
    this.selectedLeft.set([]);
    this.moveLeftState.set(ClrLoadingState.DEFAULT);
    this.moveRightState.set(ClrLoadingState.DEFAULT);
    this.moveUpDownState.set(ClrLoadingState.DEFAULT);
    this.countToMove.set(0);
  }

  private checkEnableUpDown() {
    if (this.selectedRight().length == 1)
      this.enableMove.set({
        up: this.usedArten().indexOf(this.selectedRight()[0]) != 0,
        down: this.usedArten().indexOf(this.selectedRight()[0]) != this.usedArten().length - 1,
      });
    else this.enableMove.set({ up: false, down: false });
  }

  private extractUsed() {
    this.notUsedArten.set(differenceBy(this.allArten(), this.usedArten(), 'ID'));
  }
  //#endregion
}
